/**
 * Purpose of this file is to take in all the values of the sort-filter-root element
 * (basically all data attributes, minus their "data-" part), and return an object
 * that has made sense of these values.
 * @param {*} config
 */

import utils from '../../common/utils.js';
import axios from 'axios';
import WidgetOptions from '../../common/WidgetOptions.js';
import Solr from '../services/Solr.js';
import TaxOptions from '../services/TaxOptions.js';
import PostModel from './PostModel.js';
import config from '../../common/config.js';

// normal mode - load from SOLR
const MODE_STANDARD = 'standard';
// static mode - skip SOLR, use supplied static data
const MODE_STATIC   = 'static';

const MODES = [
	MODE_STANDARD,
	MODE_STATIC
];

const $ = window && window.jQuery;
class WidgetModel {

	// debug mode
	debug = config.debug

	// displayed in editor
	inEditor = false;

	// is hidden? make leander SOLR queries
	hidden = false;

	id =
		'cc-widget-' +
		Math.floor((1 + Math.random()) * 0x10000)
			.toString(16)
			.substring(1);

	// mode of running - standard default
	mode = MODE_STANDARD;

	// services/Solr instance
	solr;

	// services/TaxOptions instance
	taxOptions;

	// WidgetOptions instance
	options = {};

	// Static SOLR response url for backup use
	staticUrl;

	// Static SOLR response JS object, for backup use
	staticData;

	// Flag for if using static mode
	isStatic = false;

	// options async load promise
	onLoadResolve;
	onLoadReject;
	onLoadPromise;

	// base values
	ready = false;
	firstQuery = true;

	// total records on first fetch
	total = 0;

	// properties grabbed from WidgetOptions
	template;
	subTemplate;
	filters;
	contentFields;
	display;

	pagination = false;
	pageSize = 10;
	limit = 0;
	columns = 1;
	columnsOverride = 0;

	// TP version
	tpVersion = 4;

	/**
	 * This is the Widget constructor
	 * @param {object} options Expected to be all data attributes on root element
	 */
	constructor(attributeOptions) {

		this.inEditor = attributeOptions.inEditor;
		this.hidden = attributeOptions.hidden;
		this.mode = attributeOptions.mode;
		this.hideForm = attributeOptions.hideForm;
		this.hideFilters = attributeOptions.hideFilters;
		this.hideImages = attributeOptions.hideImages;
		this.hideDates = attributeOptions.hideDates;
		this.hideContent = attributeOptions.hideContent;
		this.hideAlts = attributeOptions.hideAlts;
		this.hidePagination = attributeOptions.hidePagination;
		this.limitContent = attributeOptions.limitContent;
		this.limitTitle = attributeOptions.limitTitle;
		this.limit = attributeOptions.limit;

		// other properties
		this.hasForm = false;

		// Priority of options set:
		// 1. widget data attributes
		// 2. widget src config file
		// 3. widget default options

		// on load promise - share resolve and reject
		this.onLoadPromise = new Promise((resolve, reject) => {
			this.onLoadResolve = resolve;
			this.onLoadReject = reject;
		}).then(() => {
			this.ready = true;
		});

		// set tpVersion 5 if 5.0 assets are found
		if ($ && $('link[href*="/TemplatePackage/5.0/"]').length) {
			this.tpVersion = 5;
		}

		attributeOptions = Object.assign({}, attributeOptions);

		// 'id' - can be passed to the parent object via attribute 'data-id'
		if (attributeOptions.id) {
			this.id = utils.cleanString(attributeOptions.id);
		}

		// 'mode' - optional attribute to set running mode
		if (attributeOptions.mode && -1 < MODES.indexOf(attributeOptions.mode)) {
			this.mode = attributeOptions.mode;
			config.debug && console.info('cc: mode = ' + this.mode);
			this.isStatic = (MODE_STATIC === this.mode);
		}
		// 'static' stores the backup SOLR response value, in JS object or url form
		if (attributeOptions.static) {
			let staticValue = attributeOptions.static;
			config.debug && console.info( 'cc: static data: ', staticValue );
			if ( 'string' === typeof staticValue ) {
				// attempt to parse as JSON string
				try {
					this.staticData = Object.assign({}, JSON.parse(staticValue));
				} catch (error) {
					this.staticUrl = staticValue;
				}
			} else if ( utils.isObject( staticValue ) ) {
				this.staticData = staticValue;
			}
		}

		// allow template override
		if (attributeOptions.template) {
			this.template = utils.cleanString(attributeOptions.template);
		}
		// allow subTemplate override
		if (attributeOptions.subtemplate) {
			this.subTemplate = utils.cleanString(attributeOptions.subtemplate);
		} else if (attributeOptions.theme) {
			this.subTemplate = utils.cleanString(attributeOptions.theme);
		}

		// allow columns override
		if (attributeOptions.columns) {
			this.columnsOverride = utils.toNumber(attributeOptions.columns) || this.columns;
		}

		// 'options' recieves a stringified object or a full JS object
		if (attributeOptions.options) {
			if (utils.isObject(attributeOptions.options)) {
				let options = attributeOptions.options;
				this.setOptions(options);
				return;
			} else {
				try {
					let options = Object.assign({}, JSON.parse(attributeOptions.options));
					this.setOptions(options);
					return;
				} catch (error) {
					config.debug && console.error('cc: Could not parse options attribute');
				}
			}
		}

		// 'src' refers to a JSON file that contains the widget options
		if (attributeOptions.config || attributeOptions.src) {
			let configFile = attributeOptions.config || attributeOptions.src;
			axios
				.get(configFile)
				.then((response) => {
					// first options
					let options = Object.assign({}, response.data);
					this.setOptions(options);
				})
				.catch((e) => {
					config.debug && console.error('cc: failed to load config: ', configFile, e);
					// this.setOptions(attributeOptions);
					this.onLoadReject(e);
				});
			return;
		}

		// If we got here, no config available
		// this.setOptions( attributeOptions );
		this.onLoadReject();
	}

	// recieves options file and sets options
	setOptions(options) {
		// ensure structure of options
		this.options = new WidgetOptions(options);
		config.debug && console.info('cc widget Options: ', this.options, this.widgetId	);

		this.pageSize = Math.min(utils.toNumber(this.options.display.pageSize), 100);
		this.pagination = Boolean(!!this.pageSize && this.options.display.pagination && !this.hidePagination);
		if (!this.limit) {
			this.limit = Math.min(utils.toNumber(this.options.display.limit), 100);
		}

		if (0 < this.limit && this.limit < this.pageSize) {
			this.pageSize = this.limit;
			this.pagination = false;
		}
		let columns = utils.toNumber(this.options.display.columns);
		this.columns = Math.max(1, Math.min(columns, 4));
		if (this.columnsOverride) {
			this.columns = this.columnsOverride;
		}

		// update model properties from options
		if (!this.template) {
			this.template = this.options.template?.type || this.options.template || 'list';
		}
		if (!this.subTemplate) {
			this.subTemplate = this.options.subTemplate?.type || this.options.subTemplate || '';
		}

		this.filters = [];
		this.contentFields = Object.assign(this.options.contentFields);
		this.display = Object.assign(this.options.display);

		// get taxOptions service for handling filters
		// must come before solr service, as solr uses taxOptions service
		// @TODO: move to instantiate in SOLR
		this.taxOptions = new TaxOptions(this);

		// get Solr service
		this.solr = new Solr(this);

		this.taxOptions.whenReady().then(()=>{
			this.onLoadResolve();
		}).catch(error => {
			config.debug && console.error('cc:' + this.id + ' failed to load taxOptions');
			this.onLoadReject(error);
		})
	}

	getUrl() {
		return this.pageUrl;
	}

	/**
	 * Return promise that resolves once model is ready / options loaded
	 * @returns Promise
	 */
	whenReady() {
		return this.onLoadPromise;
	}

	/**
	 * Query posts with optional params
	 * @param {object} params
	 * @return {Promise}
	 */
	getPosts(params) {
		params = Object.assign({}, params);
		return new Promise((resolve, reject) => {
			// static mode
			if (MODE_STATIC === this.mode) {
				config.debug && console.info('cc:' + this.id + ': loading static mode');
				return this.getStaticPosts().then((postsResponse) => {
					return resolve(postsResponse);
				}).catch(error => {
					reject(error);
				})
			}
			// standard solr mode
			return this.solr.query(params).then((solrResponse) => {
				let postsResponse = this.getPostsResponse(solrResponse, params);
				this.firstQuery = false;
				return resolve(postsResponse);
			}).catch(error => {

				// if we have a static backup use that
				if (this.staticData || this.staticUrl) {
					config.debug && console.info('cc:' + this.id + ': failed solr request, loading static :', error);
					return this.getStaticPosts().then(postsResponse => {
						return resolve(postsResponse);
					}).catch(staticPostsError => {
						console.error( 'cc: failed to load static data: ', staticPostsError );
						reject([error, staticPostsError]);
					})
				} else {
					console.error( 'cc: request error:', error );
					return reject(error);
				}
			});
		});
	}

	/**
	 * Fetches a static data file / object with SOLR response date
	 * @returns Promise
	 */
	getStaticPosts() {
		return new Promise((resolve, reject) => {
			// Getting static posts, so you're static
			this.isStatic = true;

			if (this.staticData) {
				let solrResponse = this.solr.loadStaticData(this.staticData);
				let postsResponse = this.getPostsResponse(solrResponse);
				return resolve(postsResponse);
			}

			if (this.staticUrl) {
				axios
					.get(this.staticUrl)
					.then((response) => {
						let data = response?.data;

						// ensure we have a valid JSON object
						if (!utils.isObject(data)) {
							console.error('cc: static file cannot be parsed', this.staticUrl);
							return reject('cannot parse ' + this.staticUrl);
						}
						this.staticData = data;
						let solrResponse = this.solr.loadStaticData(this.staticData);
						let postsResponse = this.getPostsResponse(solrResponse);
						return resolve(postsResponse);
					})
					.catch((error) => {
						console.error('cc: failed to load static file: ', error);
						return reject(error);
					});
				return;
			}

			// shouldn't get here, but gotta throw something.
			return reject('cc: no static data set');
		});
	}

	/**
	 * Given a SOLR / getPosts response, handle response
	 * @param {object} solrResponse Response from SOLR service
	 * @param {object} params       Query params
	 */
	getPostsResponse(solrResponse, params) {
		// only set this once - gets total set size
		this.total = utils.toNumber(solrResponse.total);

		// if in editor, keep updating total and report to rest of page
		if (this.inEditor && $) {
			$('content-collection-editor:first').trigger('ccTotal', this.total);
		}

		let pages = 1;
		if (this.pagination) {
			if( this.limit ) {
				let totalPages = Math.min(solrResponse.total, this.limit);
				pages = Math.ceil(totalPages / this.pageSize);
			} else {
				pages = Math.ceil(solrResponse.total / this.pageSize);
			}
		}

		// update widgetModel filters
		if (solrResponse.filters) {
			this.filters = solrResponse.filters;
		}

		// finally can answer if a form is available
		this.hasForm = !this.hideForm && !!this.filters?.length

		let postsResponse = {
			posts: [],
			total: this.total,
			pages,
			page: params?.page || 1,
			filters: solrResponse.filters,
		};

		solrResponse.results.forEach((result) => {
			let post = new PostModel(result, this);
			if (post) {
				postsResponse.posts.push(post);
			}
		});

		return postsResponse;
	}

}

export default WidgetModel;
